import { useEffect, useRef, useState } from 'react';
import { HelpCircle, ExternalLink, Send, CheckCircle, AlertCircle, Star } from 'lucide-react';
import { useForm } from '@tanstack/react-form';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { submitFeedback, openExternalUrl } from '../services/feedbackApi';
import { feedbackDefaultValues, type FeedbackUserMode } from '../types/feedback';
import { useHelpFeedbackStore } from '../store/helpFeedbackStore';
import { useLicenseStore } from '../../../core/licensing/store/licenseStore';
import { useAuthStore } from '../../../core/licensing/store/authStore';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { reviewUrl } from '../../review-prompt/reviewPromptConstants';
import { buildEnvironmentFooter, collectFeedbackEnvironment } from '../services/feedbackContext';
import { isBrowserMode } from '../../browser-mode/browserModeUtils';

import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
type SubmitState = 'idle' | 'loading' | 'success' | 'error';

const HELP_UTM = 'utm_source=app&utm_medium=referral&utm_campaign=help-feedback-dialog';

const DOCS_LINKS = [
  { label: 'Documentation Home', url: `https://jamsql.com/docs/?${HELP_UTM}` },
  { label: 'Schema Compare', url: `https://jamsql.com/docs/schema-compare?${HELP_UTM}` },
  { label: 'Data Compare', url: `https://jamsql.com/docs/data-compare?${HELP_UTM}` },
  { label: 'AI Integrations & MCP', url: `https://jamsql.com/docs/ai-integrations-mcp?${HELP_UTM}` },
  { label: 'Migrate from Azure Data Studio', url: `https://jamsql.com/docs/migrate-from-azure-data-studio?${HELP_UTM}` },
  { label: 'Legal (EULA, Notices, License)', url: `https://jamsql.com/legal/?${HELP_UTM}` },
];

/**
 * Get the user's current mode for feedback tracking.
 * Returns 'trial' if on active trial, 'pro' if subscribed, 'personal' otherwise.
 */
function getUserMode(): FeedbackUserMode {
  const licenseStore = useLicenseStore.getState();
  const trialState = licenseStore.getTrialState();

  if (trialState === 'active') {
    return 'trial';
  }

  if (licenseStore.isPro()) {
    return 'pro';
  }

  return 'personal';
}

export function HelpFeedbackDialog() {
  const { track } = useAnalytics();
  const open = useHelpFeedbackStore((s) => s.isOpen);
  const setOpen = useHelpFeedbackStore((s) => s.setOpen);
  const prefillMessage = useHelpFeedbackStore((s) => s.prefillMessage);
  const prefillPrefix = useHelpFeedbackStore((s) => s.prefillPrefix);
  const prefillToken = useHelpFeedbackStore((s) => s.prefillToken);
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);

  // The web demo installs an electronAPI *shim*, so window.electronAPI alone
  // is not a desktop signal — the isBrowserMode() gate is required.
  const isDesktop = Boolean(window.electronAPI) && !isBrowserMode();

  const form = useForm({
    defaultValues: feedbackDefaultValues,
    onSubmit: async ({ value }) => {
      setSubmitState('loading');
      setErrorMessage('');

      // Get mode and userId at submit time
      const mode = getUserMode();
      const userId = useAuthStore.getState().user?.id ?? null;

      // Environment details for support triage (app version, OS, engines in
      // use — exactly what the note next to the Send button discloses).
      // Appended to the message text rather than sent as new top-level fields
      // so the backend contract stays untouched.
      const environment = isDesktop
        ? await collectFeedbackEnvironment().catch(() => null)
        : null;
      const environmentFooter = environment ? buildEnvironmentFooter(environment) : '';

      // Prepend the read-only context prefix (e.g. "Feedback on the Schema
      // Mirror feature:") so the submitted feedback carries the feature tag
      // even though the user can't edit it in the textarea.
      const submittedMessage = (prefillPrefix
        ? `${prefillPrefix.trim()}\n\n${value.message}`
        : value.message) + environmentFooter;
      const result = await submitFeedback({
        email: value.email,
        message: submittedMessage,
        timestamp: new Date().toISOString(),
        // Real version when known; legacy 'desktop'/'web' markers otherwise.
        // The web demo (shimmed electronAPI) correctly reports 'web' now.
        appVersion:
          environment && environment.appVersion !== 'unknown'
            ? environment.appVersion
            : isDesktop ? 'desktop' : 'web',
        mode,
        userId,
      });

      if (result.success) {
        setSubmitState('success');
        form.reset();
        // Auto-close after showing success message
        setTimeout(() => {
          setSubmitState('idle');
        }, 3000);
      } else {
        setSubmitState('error');
        setErrorMessage(result.error || 'Failed to submit feedback');
      }
    },
  });

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      track('toolbar:help_opened');
      // Prefill email from logged-in user
      const userEmail = useAuthStore.getState().user?.email;
      if (userEmail && !form.getFieldValue('email')) {
        form.setFieldValue('email', userEmail);
      }
    } else {
      // Reset state when dialog closes
      setSubmitState('idle');
      setErrorMessage('');
      form.reset();
    }
  };

  // Apply message prefill + focus whenever the dialog is opened programmatically
  // with a prefill (token increments on every programmatic open).
  useEffect(() => {
    if (!open || prefillToken === 0 || !prefillMessage) return;
    form.setFieldValue('message', prefillMessage);
    // Defer focus until after Radix mounts the dialog content.
    const id = window.setTimeout(() => {
      const textarea = messageTextareaRef.current;
      if (textarea) {
        textarea.focus();
        const end = prefillMessage.length;
        textarea.setSelectionRange(end, end);
      }
    }, 50);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillToken, open]);

  const handleLinkClick = (url: string) => {
    openExternalUrl(url);
  };

  const handleRateClick = () => {
    track('feature:review_link_click', { source: 'help-dialog' });
    openExternalUrl(reviewUrl('help-feedback-dialog'));
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 active:scale-[0.96] transition-[color,box-shadow,scale] relative before:absolute before:inset-x-0 before:-inset-y-1 before:content-['']"
          title="Help & Feedback (F1)"
          data-testid="help-feedback-button"
        >
          <HelpCircle className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] p-0 gap-0" data-testid="help-feedback-dialog">
        <DialogHeader className="px-6 py-4 border-b border-border">
          <DialogTitle>Help & Feedback</DialogTitle>
        </DialogHeader>

        <div className="p-6 space-y-6">
          {/* Documentation Links Section */}
          <section>
            <h3 className="text-sm font-semibold mb-3 text-foreground">Documentation</h3>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              {DOCS_LINKS.map((link) => (
                <button
                  key={link.url}
                  onClick={() => handleLinkClick(link.url)}
                  className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-md hover:bg-muted text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ExternalLink className="h-4 w-4 shrink-0" />
                  <span>{link.label}</span>
                </button>
              ))}
            </div>
            <button
              onClick={handleRateClick}
              className="mt-1 flex items-center gap-2 w-full text-left px-3 py-2 rounded-md hover:bg-amber-500/10 text-sm text-warning hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
              data-testid="rate-jam-sql-link"
            >
              <Star className="h-4 w-4 shrink-0 fill-current" />
              <span>Rate Jam SQL Studio</span>
            </button>
          </section>

          <div className="border-t border-border" />

          {/* Feedback Form Section */}
          <section>
            <h3 className="text-sm font-semibold mb-3 text-foreground">Send Feedback</h3>

            {submitState === 'success' ? (
              <div className="flex items-center gap-3 p-4 rounded-md bg-green-500/10 text-green-600">
                <CheckCircle className="h-5 w-5" />
                <span>Thank you for your feedback!</span>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  form.handleSubmit();
                }}
                className="space-y-4"
              >
                <form.Field name="email">
                  {(field) => (
                    <div className="space-y-1.5">
                      <Label htmlFor="feedback-email">Email</Label>
                      <Input
                        id="feedback-email"
                        type="email"
                        placeholder="your@email.com"
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        disabled={submitState === 'loading'}
                        data-testid="feedback-email-input"
                      />
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-xs text-destructive">{field.state.meta.errors[0]}</p>
                      )}
                    </div>
                  )}
                </form.Field>

                <form.Field name="message">
                  {(field) => (
                    <div className="space-y-1.5">
                      <Label htmlFor="feedback-message">Message</Label>
                      {prefillPrefix && (
                        <div
                          className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground select-text"
                          data-testid="feedback-message-prefix"
                          aria-readonly="true"
                          title="This context is added automatically and cannot be edited."
                        >
                          {prefillPrefix}
                        </div>
                      )}
                      <Textarea
                        id="feedback-message"
                        ref={messageTextareaRef}
                        placeholder="Describe your feedback, bug report, or feature request..."
                        rows={4}
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        disabled={submitState === 'loading'}
                        data-testid="feedback-message-input"
                      />
                      {field.state.meta.errors.length > 0 && (
                        <p className="text-xs text-destructive">{field.state.meta.errors[0]}</p>
                      )}
                    </div>
                  )}
                </form.Field>

                {submitState === 'error' && (
                  <div className="flex items-center gap-2 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                <div className="flex items-center justify-between gap-4">
                  {isDesktop ? (
                    <p className="text-[11px] leading-snug text-muted-foreground">
                      Basic app info (version, OS, database engines in use) is
                      attached to help us debug — never queries or server names.
                    </p>
                  ) : (
                    <span />
                  )}
                  <Button
                    type="submit"
                    className="shrink-0"
                    disabled={submitState === 'loading'}
                    data-testid="feedback-submit-button"
                  >
                    {submitState === 'loading' ? (
                      <>
                        <RandomDotmSpinner size={16} className="mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Feedback
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </section>

          {/* Support Contact — for users who prefer plain email over the form */}
          <div className="text-xs text-muted-foreground text-center pt-2">
            Prefer email? Contact us at{' '}
            <button
              onClick={() => {
                track('feature:support_email_click', { source: 'help-dialog' });
                handleLinkClick('mailto:support@jamsql.com');
              }}
              className="text-primary hover:underline"
              data-testid="support-email-link"
            >
              support@jamsql.com
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
